'use client';

/**
 * VoxMicLauncher - VOX mic v hero inpute.
 * idle  = cierny kruh: zlata soundwave + pendulum rim + radar sweep
 * hover = pill sa roztiahne a zaleje zlatou plazmou; mala wave aj text zmiznu,
 *         namiesto nich siroka animovana soundwave (pozvanka na rozhovor)
 * klik  = LIVE mod: pill ostava roztiahnuty sirsie, plazma bezi, bars behaju
 *         intenzivne (--lvl:1 + .voxl-live), vnutri pribudnu biele kruzky
 *         mute mikrofonu a repro (stub, kym Kiso nenapoji VOX engine).
 *         Klik mimo kruzkov LIVE ukonci.
 * Vsetky prechody s --ease-rx.
 */
import { useEffect, useRef, useState } from 'react';
import { useVoxCtx } from '../lib/VoxProvider';
import { cn } from '@/shadcn/lib/utils';

function Bars({ bars, viewW, className }: { bars: Array<[number, number, number, number, number]>; viewW: number; className?: string }) {
    return (
        <svg viewBox={`0 0 ${viewW} 24`} fill="none" className={className} aria-hidden>
            {bars.map(([x, h, k, dur, delay]) => (
                <rect
                    key={x}
                    x={x}
                    y={12 - h / 2}
                    width="2.2"
                    height={h}
                    rx="1.1"
                    fill="currentColor"
                    className="vox-bar"
                    style={{
                        ['--k' as string]: k,
                        animationDuration: dur + 's',
                        animationDelay: delay + 's',
                        transformBox: 'fill-box',
                        transformOrigin: 'center',
                    }}
                />
            ))}
        </svg>
    );
}

const SMALL: Array<[number, number, number, number, number]> = [
    [1.6, 6, 0.9, 1.9, 0], [6.6, 10, 0.7, 1.5, 0.25], [11.6, 15, 0.5, 1.7, 0.5],
    [16.6, 18, 0.35, 1.4, 0.1], [21.6, 15, 0.5, 1.8, 0.65], [26.6, 10, 0.7, 1.55, 0.35],
    [31.6, 6, 0.9, 2.0, 0.8],
];
const WIDE: Array<[number, number, number, number, number]> = [
    [1.6, 5, 1.0, 1.8, 0], [6.6, 9, 0.8, 1.5, 0.3], [11.6, 13, 0.6, 1.7, 0.55],
    [16.6, 17, 0.45, 1.35, 0.15], [21.6, 19, 0.35, 1.5, 0.7], [26.6, 17, 0.45, 1.65, 0.4],
    [31.6, 13, 0.6, 1.45, 0.85], [36.6, 9, 0.8, 1.7, 0.2], [41.6, 13, 0.6, 1.55, 0.6],
    [46.6, 9, 0.8, 1.4, 0.05], [51.6, 5, 1.0, 1.9, 0.45],
];

/* Lucide outline (sada I1 z labu) */
function MicIcon({ muted }: { muted: boolean }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-[14px]" aria-hidden>
            {muted ? (
                <>
                    <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
                    <path d="M15 9.34V5a3 3 0 0 0-5.94-.6" />
                    <path d="M17 16.95A7 7 0 0 1 5 12v-2" />
                    <path d="M19 10v2a7 7 0 0 1-.11 1.23" />
                    <line x1="12" y1="19" x2="12" y2="22" />
                    <line x1="2" y1="2" x2="22" y2="22" />
                </>
            ) : (
                <>
                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="22" />
                </>
            )}
        </svg>
    );
}

function SpeakerIcon({ off }: { off: boolean }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-[14px]" aria-hidden>
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            {off ? (
                <>
                    <line x1="22" y1="9" x2="16" y2="15" />
                    <line x1="16" y1="9" x2="22" y2="15" />
                </>
            ) : (
                <>
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </>
            )}
        </svg>
    );
}

/** 'hero' = responzivny pill v hero inpute; 'chat' = fixnych 38px v chat docku (uzsi panel) */
export function VoxMicLauncher(props: { variant?: 'hero' | 'chat' } = {}) {
    const chat = props.variant === 'chat';
    const [muted, setMuted] = useState(false);
    const [spkOff, setSpkOff] = useState(false);

    // Skutocny engine, ked je stranka obalena VoxProviderom. Bez neho ostava
    // povodne lokalne spravanie, aby sa komponent dal pouzit aj samostatne.
    const vox = useVoxCtx();
    const [localLive, setLocalLive] = useState(false);
    // Tlacidlo sleduje stav MIKROFONU (phase), nie rozhovoru. `isActive` je podla
    // kontraktu pravdive od prveho startu az po reset - teda aj po zastaveni,
    // kym su v rozhovore spravy. Tlacidlo by sa uz nikdy nevratilo do pokoja.
    const live = vox ? vox.phase !== 'idle' : localLive;

    const btnRef = useRef<HTMLButtonElement | null>(null);

    /**
     * Vlna riadena mikrofonom.
     *
     * Povodne ciarky bezali stalu 1,7-sekundovu animaciu a hlasitost im len
     * menila rozkmit - vyzeralo to rovnako, ci clovek hovoril alebo mlcal.
     * Pocas rozhovoru preto CSS animaciu vypneme a kazdej ciarke nastavime
     * vysku priamo zo spektra mikrofonu.
     *
     * Vsetko sa zapisuje do style, nie do stavu: pri 60 snimkach za sekundu by
     * kazde prekreslenie tahalo celu stranku.
     */
    useEffect(() => {
        const btn = btnRef.current;
        if (!btn) return;
        const ciarky = Array.from(btn.querySelectorAll<SVGRectElement>('.vox-bar'));

        if (!vox || !live) {
            btn.style.setProperty('--lvl', '0');
            // Vratime povodne dychanie
            ciarky.forEach((c) => {
                c.style.animation = '';
                c.style.transform = '';
            });
            return;
        }

        ciarky.forEach((c) => (c.style.animation = 'none'));

        let raf = 0;
        // Vyhladenie, aby ciarky neposkakovali medzi snimkami
        const vyhladene = new Array(ciarky.length).fill(0);

        const tik = () => {
            const spektrum = vox.getBars();
            const l = vox.getLevel();
            btn.style.setProperty('--lvl', String(Math.min(1, l)));

            ciarky.forEach((c, i) => {
                // Ciarok je 7 alebo 11, spektrum ma 28 pasiem - vzorkujeme rovnomerne
                const od = Math.floor((i / ciarky.length) * spektrum.length);
                const do_ = Math.max(od + 1, Math.floor(((i + 1) / ciarky.length) * spektrum.length));
                let max = 0;
                for (let j = od; j < do_; j++) max = Math.max(max, spektrum[j] || 0);

                vyhladene[i] = vyhladene[i] * 0.55 + max * 0.45;
                // Spodok 0.18, aby vlna v tichu nezmizla uplne
                const scale = 0.18 + Math.min(1, vyhladene[i] * 1.35) * 1.5;
                c.style.transform = `scaleY(${scale.toFixed(3)})`;
            });

            raf = requestAnimationFrame(tik);
        };
        raf = requestAnimationFrame(tik);
        return () => {
            cancelAnimationFrame(raf);
            ciarky.forEach((c) => {
                c.style.animation = '';
                c.style.transform = '';
            });
        };
    }, [vox, live]);

    return (
        <button
            ref={btnRef}
            type="button"
            aria-label={live ? 'Ukončiť rozhovor' : 'Spustiť rozhovor s Realicom'}
            aria-pressed={live}
            onClick={() => {
                if (!vox) return setLocalLive((v) => !v);
                if (vox.phase !== 'idle') {
                    vox.stop();
                    return;
                }
                // Rozhovor sa zobrazuje priamo v smart liste, ktora sa rozbali
                // smerom dole - samostatny dok sa uz neotvara.
                vox.start();
            }}
            className={cn(
                'group/voxl relative overflow-hidden rounded-full cursor-pointer shrink-0',
                // v chate je prvok maly a panel uzky - plazma na hover je stlmena (voxl-calm)
                chat && 'voxl-calm',
                chat ? 'h-[38px]' : 'h-[32px] md:h-[35px] xl:h-[42px]',
                'flex items-center justify-center',
                'transition-all duration-[380ms] ease-[var(--ease-rx)]',
                'active:scale-[.98]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2',
                live
                    ? cn(
                          chat ? 'w-[132px]' : 'w-[123px] md:w-[128px] xl:w-[142px]',
                          'bg-[#e8a800]',
                          'voxl-live [--lvl:1]',
                      )
                    : cn(
                          chat ? 'w-[38px] hover:w-[124px]' : 'w-[32px] md:w-[35px] xl:w-[42px] hover:w-[118px] md:hover:w-[122px] xl:hover:w-[130px]',
                          'bg-[#0a0a0a] hover:bg-[#e8a800]',
                          '[--lvl:0] hover:[--lvl:0.55]',
                      ),
            )}
        >
            {/* radar + pendulum rim - len na ciernom idle */}
            <span
                aria-hidden
                className={cn(
                    'voxl-radar pointer-events-none absolute inset-[2px] rounded-full',
                    'transition-opacity duration-[380ms] ease-[var(--ease-rx)]',
                    live ? 'opacity-0' : 'group-hover/voxl:opacity-0',
                )}
            />
            <span
                aria-hidden
                className={cn(
                    'voxl-rim pointer-events-none absolute inset-0 rounded-full',
                    'transition-opacity duration-[380ms] ease-[var(--ease-rx)]',
                    live ? 'opacity-0' : 'group-hover/voxl:opacity-0',
                )}
            />

            {/* liquid plazma - hover/live ignition */}
            <span
                aria-hidden
                className={cn(
                    'voxl-deep-a pointer-events-none absolute -top-[120%] -left-[80%] size-[320%]',
                    'transition-opacity duration-[450ms] ease-[var(--ease-rx)]',
                    live ? 'opacity-100' : 'opacity-0 group-hover/voxl:opacity-100',
                )}
            />
            <span
                aria-hidden
                className={cn(
                    'voxl-deep-b pointer-events-none absolute -top-[150%] -left-[30%] size-[320%]',
                    'transition-opacity duration-[450ms] ease-[var(--ease-rx)] delay-[60ms]',
                    live ? 'opacity-100' : 'opacity-0 group-hover/voxl:opacity-100',
                )}
            />
            <span
                aria-hidden
                className={cn(
                    'voxl-deep-c pointer-events-none absolute -top-[70%] -left-[50%] size-[240%]',
                    'transition-opacity duration-[450ms] ease-[var(--ease-rx)] delay-[120ms]',
                    live ? 'opacity-80' : 'opacity-0 group-hover/voxl:opacity-80',
                )}
            />

            <span className={cn('relative z-[2] flex items-center w-full h-full', live ? 'justify-start' : 'justify-center')}>
                {/* LIVE ovladanie - biele kruzky ako Homes: mute mic + repro */}
                <span
                    className={cn(
                        'flex items-center gap-[6px] overflow-hidden',
                        'transition-all duration-[340ms] ease-[var(--ease-rx)]',
                        live
                            ? 'max-w-[70px] opacity-100 ml-[4px] md:ml-[5px] xl:ml-[8px]'
                            : 'max-w-0 opacity-0 pointer-events-none',
                    )}
                >
                    <span
                        role="button"
                        tabIndex={live ? 0 : -1}
                        aria-label={muted ? 'Zapnúť mikrofón' : 'Stlmiť mikrofón'}
                        onClick={(e) => { e.stopPropagation(); setMuted((v) => !v); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setMuted((v) => !v); } }}
                        className={cn(
                            'flex items-center justify-center size-[24px] xl:size-[26px] rounded-full shrink-0',
                            'transition duration-[220ms] ease-[var(--ease-rx)]',
                            // ON = biely, OFF (stlmeny) = zoslabnuty
                            muted
                                ? 'bg-[rgba(255,255,255,.4)] text-black/45 shadow-none'
                                : 'bg-white text-black/85 shadow-[0_2px_6px_rgba(126,90,0,.25)]',
                            'hover:brightness-[1.05] hover:shadow-[0_3px_8px_rgba(126,90,0,.3)]',
                        )}
                    >
                        <MicIcon muted={muted} />
                    </span>
                    <span
                        role="button"
                        tabIndex={live ? 0 : -1}
                        aria-label={spkOff ? 'Zapnúť zvuk' : 'Vypnúť zvuk'}
                        onClick={(e) => { e.stopPropagation(); setSpkOff((v) => !v); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setSpkOff((v) => !v); } }}
                        className={cn(
                            'flex items-center justify-center size-[24px] xl:size-[26px] rounded-full shrink-0',
                            'transition duration-[220ms] ease-[var(--ease-rx)]',
                            spkOff
                                ? 'bg-[rgba(255,255,255,.4)] text-black/45 shadow-none'
                                : 'bg-white text-black/85 shadow-[0_2px_6px_rgba(126,90,0,.25)]',
                            'hover:brightness-[1.05] hover:shadow-[0_3px_8px_rgba(126,90,0,.3)]',
                        )}
                    >
                        <SpeakerIcon off={spkOff} />
                    </span>
                </span>

                {/* hover label - siroka wave je az na klik */}
                <span
                    className={cn(
                        'voxl-label overflow-hidden whitespace-nowrap',
                        'text-[12px] font-[600] text-black/90 xl:text-[13px]',
                        'transition-all duration-[340ms] ease-[var(--ease-rx)] delay-[50ms]',
                        live
                            ? 'max-w-0 opacity-0'
                            : 'max-w-0 opacity-0 group-hover/voxl:max-w-[110px] group-hover/voxl:opacity-100',
                    )}
                >
                    Skús to hlasom
                </span>

                {/* wave scena: mala zlata (idle), siroka cierna (len LIVE) */}
                <span
                    className={cn(
                        'voxl-wave relative flex items-center justify-center h-full',
                        'transition-all duration-[380ms] ease-[var(--ease-rx)]',
                        live ? 'flex-1' : 'w-[26px] group-hover/voxl:w-0',
                    )}
                >
                    <Bars
                        bars={SMALL}
                        viewW={36}
                        className={cn(
                            'voxl-bars-small absolute h-[15px] w-[23px] xl:h-[17px] xl:w-[26px]',
                            'text-[#f3c300]',
                            'transition-opacity duration-[240ms] ease-[var(--ease-rx)]',
                            live ? 'opacity-0' : 'opacity-100 group-hover/voxl:opacity-0',
                        )}
                    />
                    <Bars
                        bars={WIDE}
                        viewW={56}
                        className={cn(
                            'absolute h-[17px] w-[40px] xl:h-[19px] xl:w-[46px]',
                            'text-black/85',
                            'transition-opacity duration-[300ms] ease-[var(--ease-rx)] delay-[120ms]',
                            live ? 'opacity-100' : 'opacity-0',
                        )}
                    />
                </span>
            </span>
        </button>
    );
}
